import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  concat,
  delay,
  filter,
  forkJoin,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import {
  AttributeService,
  GroupCategoryService,
  GroupService,
  TreeService,
  UserService,
} from '../../shared';
import {
  CreateUsersResponse,
  EditGroup,
  FlattenedTreeModel,
  OrganisationGroupModel,
  ValidationStatusEnum,
} from '../../shared/models';
import { SegmentService } from '../../shared/services/segment.service';
import * as OrganizationActions from '../actions/organization.actions';
import { AppState } from '../app.state';

@Injectable()
export class OrganizationEffects {
  private store = inject<Store<AppState>>(Store);
  private actions$ = inject(Actions);
  private userService = inject(UserService);
  private treeService = inject(TreeService);
  private attributeService = inject(AttributeService);
  private groupService = inject(GroupService);
  private groupCategoryService = inject(GroupCategoryService);
  private segmentService = inject(SegmentService);

  fetchUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchUsers),
      switchMap((action) =>
        this.userService.getByCompanyId(action.companyId).pipe(
          map((users) => {
            return OrganizationActions.fetchUsersSuccess({ users });
          }),
          catchError((error) => of(OrganizationActions.fetchUsersError({ error }))),
        ),
      ),
    ),
  );

  fetchUserAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchUsersSuccess),
      switchMap((action) =>
        this.userService.getUserAttributesByCompanyId().pipe(
          map((userAttributes) => {
            return OrganizationActions.fetchUserAttributesSuccess({ userAttributes });
          }),
          catchError((error) => of(OrganizationActions.fetchUserAttributesError({ error }))),
        ),
      ),
    ),
  );

  fetchGroupUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchGroupUsers),
      switchMap((action) =>
        this.userService.getGroupUsers(action.companyId, action.groupId).pipe(
          map((users) => {
            return OrganizationActions.fetchUsersSuccess({ users });
          }),
          catchError((error) => of(OrganizationActions.fetchUsersError({ error }))),
        ),
      ),
    ),
  );

  fetchManagersUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchManagersUsers),
      switchMap((action) =>
        this.userService.getUsersByManager(action.userId).pipe(
          map((users) => {
            return OrganizationActions.fetchManagersUsersSuccess({ users });
          }),
          catchError((error) => of(OrganizationActions.fetchManagersUsersError({ error }))),
        ),
      ),
    ),
  );

  // This effect is a bit special. This will probably only be usef for segments.
  // It fetches a group, mostly to get the new user count. And return/dispatches editGroupSuccess
  // Added manager info, even if not needed, state should not be changed for manager.....
  fetchGroupOrSegment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchGroup),
      withLatestFrom(
        this.store.select((state) => state.organizationState.tree),
        this.store.select((state) => state.organizationState.activeHierarchy),
        this.store.select((state) => state.organizationState.users),
      ),
      switchMap(([action, tree, selectedHierarchyId, users]) => {
        const hierarchy = tree.find((t) => t.id === selectedHierarchyId);
        const flattenedTree = hierarchy?.flattenedTree ?? [];
        const defaultHierarchy = tree.find((t) => t.isDefault);
        const flatTree =
          flattenedTree.length > 0 ? flattenedTree : (defaultHierarchy?.flattenedTree ?? []);

        const stateGroup = this.findGroupById(flatTree, action.groupId);

        return this.groupService.get(action.groupId).pipe(
          switchMap((group) => {
            const existsInTree = !!stateGroup;

            const user = Object.values(users).find(
              (u) => u.externalId === stateGroup?.managerExternalId,
            );
            const manager = user ? { id: user.id, name: user.fullName, email: user.email } : null;

            const mainAction = existsInTree
              ? OrganizationActions.editGroupSuccess({
                  group,
                  manager,
                  currentManagerId: manager?.id,
                })
              : OrganizationActions.addGroupSuccess({
                  hierarchyId: selectedHierarchyId,
                  categoryId: group.categoryId,
                  leaf: true,
                  group,
                  parentId: 0,
                });

            // Always update the group’s users
            const updateUsers$ = this.userService
              .getGroupUsers(action.companyId, action.groupId)
              .pipe(
                map((groupUsers) =>
                  OrganizationActions.updateGroupIdsAndUserIds({
                    hierarchyId: selectedHierarchyId,
                    groupIdsAndUserIds: {
                      [action.groupId]: groupUsers.map((u) => u.id),
                    },
                  }),
                ),
              );

            return merge(of(mainAction), updateUsers$);
          }),
          catchError((error) => of(OrganizationActions.fetchGroupError({ error }))),
        );
      }),
    ),
  );

  private findGroupById(
    tree: FlattenedTreeModel<OrganisationGroupModel>[],
    targetId: number,
  ): FlattenedTreeModel<OrganisationGroupModel> | undefined {
    for (const node of tree) {
      if (node.id === targetId) {
        return node;
      }

      if (Array.isArray(node.children)) {
        const result = this.findGroupById(node.children, targetId);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  }

  fetchTree$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchTree),
      switchMap((action) =>
        this.treeService.getOrganisationTree(action.companyId).pipe(
          map((tree) => {
            return OrganizationActions.fetchTreeSuccess({ tree });
          }),
          catchError((error) => of(OrganizationActions.fetchTreeError({ error }))),
        ),
      ),
    ),
  );

  fetchGroupIdsAndUserIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchGroupIdsAndUserIds),
      switchMap((action) =>
        this.userService.getGroupsUserIds(action.hierarchyId).pipe(
          map((groupIdsAndUserIds) => {
            return OrganizationActions.fetchGroupIdsAndUserIdsSuccess({
              hierarchyId: action.hierarchyId,
              groupIdsAndUserIds,
            });
          }),
          catchError((error) => of(OrganizationActions.fetchGroupIdsAndUserIdsError({ error }))),
        ),
      ),
    ),
  );

  fetchUserIdsForGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchUserIdsForGroup),
      switchMap((action) =>
        this.userService.getGroupUserIds(action.groupId).pipe(
          map((hierarchyAndUserIds) => {
            return OrganizationActions.fetchUserIdsForGroupSuccess({
              hierarchyId: hierarchyAndUserIds.hierarchyId,
              groupId: action.groupId,
              userIds: hierarchyAndUserIds.userIds,
            });
          }),
        ),
      ),
    ),
  );

  fetchCompanyAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchCompanyAttributes),
      switchMap((action) =>
        this.attributeService.getByCompany(action.companyId).pipe(
          map((attributes) => {
            return OrganizationActions.fetchCompanyAttributesSuccess({ attributes });
          }),
          catchError((error) => of(OrganizationActions.fetchCompanyAttributesError({ error }))),
        ),
      ),
    ),
  );

  validateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.validateUser),
      switchMap((action) =>
        this.userService
          .validateManuallyEditedUser({
            companyId: action.user.companyId,
            email: action.user.email,
            externalId: action.user.externalId,
            id: action.user.id,
            phoneNumber: action.user.phoneNumber,
          })
          .pipe(
            mergeMap((fields) => {
              if (fields.length === 0) {
                const followUpAction = action.user.id
                  ? OrganizationActions.editUser({ user: action.user })
                  : OrganizationActions.addUser({ user: action.user });

                return [OrganizationActions.validateUserSuccess({ fields }), followUpAction];
              } else {
                return [OrganizationActions.validateUserFailed({ fields })];
              }
            }),
            catchError((error) => of(OrganizationActions.validateUserError({ error }))),
          ),
      ),
    ),
  );

  addUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.addUser),
      switchMap((action) =>
        this.userService.create(action.user).pipe(
          map((user) => {
            user.hierarchyId = action.user.hierarchyId;
            user.groupId = action.user.groupId;
            return OrganizationActions.addUserSuccess({ user });
          }),
          catchError((error) => of(OrganizationActions.addUserError({ error }))),
        ),
      ),
    ),
  );

  editUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editUser),
      switchMap((action) =>
        this.userService.update(action.user.id, action.user).pipe(
          map((user) => {
            return OrganizationActions.editUserSuccess({ user });
          }),
          catchError((error) => of(OrganizationActions.editUserError({ error }))),
        ),
      ),
    ),
  );

  addGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.addGroup),
      switchMap((action) =>
        this.groupService.createSubGroup(action.parentGroupId, action.group).pipe(
          mergeMap((groupHttpResponse) => {
            const createdGroup = groupHttpResponse.body;

            return [
              OrganizationActions.addGroupSuccess({
                hierarchyId: action.group.hierarchyId,
                categoryId: action.group.categoryId,
                parentId: action.parentGroupId,
                group: createdGroup,
                leaf: action.leaf,
                manager: action.manager,
              }),
            ];
          }),
          catchError((error) => {
            if (
              error?.error?.error ===
              'ExternalId already used by another group of the same category'
            ) {
              return of(
                OrganizationActions.addGroupValidationFail({
                  error: error?.error?.error,
                }),
              );
            }

            return of(OrganizationActions.addGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  addRuleBasedGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.addRuledBasedGroup),
      switchMap((action) =>
        this.groupService.createRuleBasedSubGroup(action.parentGroupId, action.group).pipe(
          map((groupHttpResponse) => {
            const createdGroup = groupHttpResponse.body;

            return OrganizationActions.addGroupSuccess({
              hierarchyId: action.hierarchyId,
              categoryId: action.group.categoryId,
              parentId: action.parentGroupId,
              group: createdGroup,
              leaf: action.leaf,
              manager: action.manager,
            });
          }),
          catchError((error) => {
            if (error?.error?.error === 'A group with the selected external ID already exists.') {
              return of(
                OrganizationActions.addGroupValidationFail({
                  error: error?.error?.error,
                }),
              );
            }

            return of(OrganizationActions.addGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  addGroupSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.addGroupSuccess),
      map(({ parentId, group }) =>
        OrganizationActions.selectRows({
          ids: [parentId, group.id],
        }),
      ),
    ),
  );

  editRuleBasedGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editRuledBasedGroup),
      switchMap((action) =>
        this.groupService.updateRuleBasedGroup(action.group.id, action.group).pipe(
          map((groupHttpResponse) => {
            return OrganizationActions.editGroupSuccess({
              group: groupHttpResponse.body,
              manager: action.manager,
              currentManagerId: action.currentManagerId,
            });
          }),
          catchError((error) => {
            if (
              error?.error?.error ===
              'ExternalId already used by another group of the same category'
            ) {
              return of(
                OrganizationActions.editGroupValidationFail({ error: error?.error?.error }),
              );
            }

            return of(OrganizationActions.editGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  editGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editGroup),
      mergeMap((action) =>
        this.groupService.update(action.group.id, action.group).pipe(
          map((groupHttpResponse) => {
            return OrganizationActions.editGroupSuccess({
              group: groupHttpResponse.body,
              manager: action.manager,
              currentManagerId: action.currentManagerId,
            });
          }),
          catchError((error) => {
            if (
              error?.error?.error ===
              'ExternalId already used by another group of the same category'
            ) {
              return of(
                OrganizationActions.editGroupValidationFail({ error: error?.error?.error }),
              );
            }

            return of(OrganizationActions.editGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  editGroupSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editGroupSuccess),
      withLatestFrom(this.store.select((state) => state.userInfo.userId)),
      filter(([action, userId]) => {
        const newManager = action.manager?.id;
        const oldManager = action.currentManagerId;

        const becameManager = newManager === userId && oldManager !== userId;
        const lostManager = oldManager === userId && newManager !== userId;

        return becameManager || lostManager;
      }),
      map(([action, userId]) => OrganizationActions.fetchManagersUsers({ userId })),
    ),
  );

  moveGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.moveGroup),
      switchMap((action) =>
        this.groupService
          .moveGroup(action.groupId, {
            hierarchyId: action.hierarchyId,
            currentParentId: action.currentParentId,
            newParentId: action.newParentId,
          })
          .pipe(
            mergeMap(() => [
              OrganizationActions.moveGroupSuccess({
                groupId: action.groupId,
                hierarchyId: action.hierarchyId,
                currentParentId: action.currentParentId,
                newParentId: action.newParentId,
              }),
              OrganizationActions.selectRows({
                ids: [action.newParentId, action.groupId],
              }),
            ]),
            catchError((error) => of(OrganizationActions.moveGroupError({ error }))),
          ),
      ),
    ),
  );

  moveGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.moveGroups),
      switchMap((action) =>
        this.groupService.moveGroups(action.request).pipe(
          switchMap(() =>
            concat(
              // First action dispatched immediately
              of(OrganizationActions.fetchTree({ companyId: action.companyId })),

              // Second action dispatched 500ms later so the tree component have time to render
              of(
                OrganizationActions.selectRows({
                  ids: [
                    action.request.moveGroupInfo[0]?.moveGroup?.newParentId,
                    ...action.request.moveGroupInfo.map((g) => g.groupId),
                  ],
                }),
              ).pipe(delay(500)),
            ),
          ),
          catchError((error) => of(OrganizationActions.moveGroupError({ error }))),
        ),
      ),
    ),
  );

  fetchAllowedMoveCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchAllowedMoveCategories),
      switchMap((action) =>
        this.groupCategoryService.getAllowedMoveCategories(action.companyId).pipe(
          map((allowedCategories) => {
            return OrganizationActions.fetchAllowedMoveCategoriesSuccess({
              allowedMoveCategories: allowedCategories,
            });
          }),
          catchError((error) => {
            return of(OrganizationActions.fetchAllowedMoveCategoriesError({ error }));
          }),
        ),
      ),
    ),
  );

  moveUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.moveUsers),
      switchMap((action) =>
        this.userService
          .move({
            companyId: action.companyId,
            hierarchyId: action.hierarchyId,
            newGroupId: action.newGroupId,
            userIds: action.userIds,
          })
          .pipe(
            mergeMap((users) => [
              OrganizationActions.moveUsersSuccess({
                hierarchyId: action.hierarchyId,
                groupId: action.newGroupId,
                userIds: action.userIds,
                usersAfterMove: users,
              }),
              OrganizationActions.selectRows({
                ids: [action.newGroupId, ...action.userIds],
              }),
            ]),
            catchError((error) => of(OrganizationActions.moveUsersError({ error }))),
          ),
      ),
    ),
  );

  deleteSegment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteSegment),
      switchMap((action) =>
        this.segmentService.deleteSegment({ id: action.groupId }).pipe(
          map((_) => {
            return OrganizationActions.deleteGroupSuccess({ groupId: action.groupId });
          }),
          catchError((error) => {
            return of(OrganizationActions.deleteGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  deleteGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteGroup),
      switchMap((action) =>
        this.groupService.delete(action.groupId).pipe(
          map((_) => {
            return OrganizationActions.deleteGroupSuccess({ groupId: action.groupId });
          }),
          catchError((error) => {
            return of(OrganizationActions.deleteGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  deleteTeamAndRemoveAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteTeamAndRemoveAttributes),
      switchMap((action) => {
        // 1. Remove attributes (1 and 2) from all team members
        const removeAttributes$ =
          action.userIds.length > 0
            ? this.userService.removeUsersAttributes({
                userIds: action.userIds,
                attributeIds: action.attributeIds,
              })
            : of([] as any[]);

        return removeAttributes$.pipe(
          // 2. Delete the group
          switchMap((updatedUsers) =>
            this.groupService.delete(action.groupId).pipe(
              mergeMap(() => {
                const resultActions: (
                  | ReturnType<typeof OrganizationActions.deleteGroupSuccess>
                  | ReturnType<typeof OrganizationActions.editUsersSuccess>
                )[] = [OrganizationActions.deleteGroupSuccess({ groupId: action.groupId })];

                if (updatedUsers.length > 0) {
                  resultActions.push(OrganizationActions.editUsersSuccess({ users: updatedUsers }));
                }

                return resultActions;
              }),
            ),
          ),
          catchError((error) => of(OrganizationActions.deleteGroupError({ error }))),
        );
      }),
    ),
  );

  createUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.createUsers),
      switchMap((action) =>
        this.userService.createUsers(action.request).pipe(
          map((response) => {
            return OrganizationActions.createUsersSuccess({ response });
          }),
          catchError((error) => of(OrganizationActions.createUsersError({ error }))),
        ),
      ),
    ),
  );

  createRuleBasedTeamWithUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.createRuleBasedTeamWithUsers),
      switchMap((action) =>
        // 1. Create the rule-based sub group (without manager)
        this.groupService.createRuleBasedSubGroup(action.parentGroupId, action.group).pipe(
          switchMap((groupHttpResponse) => {
            const createdGroup = groupHttpResponse.body;
            const managerIsNew = action.managerEmail && !action.manager;

            // 2a. Create member users (as members of the new team)
            const createMembers$ =
              action.usersRequest && action.usersRequest.users.length > 0
                ? this.userService.createUsers({
                    users: action.usersRequest.users,
                    groupId: createdGroup.id,
                  })
                : of(undefined as CreateUsersResponse | undefined);

            // 2b. Create manager user separately (if new)
            // If manager is also a member → groupId is createdGroup.id (belongs to team)
            // If manager is NOT a member → groupId is parentGroupId (belongs to parent/top group)
            const createManager$ = managerIsNew
              ? this.userService.createUsers({
                  users: [action.managerUserRequest ?? { email: action.managerEmail }],
                  groupId: action.managerIsAlsoMember ? createdGroup.id : action.parentGroupId,
                })
              : of(undefined as CreateUsersResponse | undefined);

            return forkJoin({
              membersResponse: createMembers$,
              managerResponse: createManager$,
            }).pipe(
              // 3. getForEdit and update the group with manager (if any)
              switchMap(({ membersResponse, managerResponse }) => {
                if (!action.managerEmail) {
                  return of({
                    membersResponse,
                    managerResponse,
                    updatedGroup: undefined,
                    resolvedManager: undefined,
                  });
                }

                // Resolve manager from existing user or just-created users
                let resolvedManager: { id: number; name: string; email: string } | undefined;

                if (action.manager) {
                  resolvedManager = action.manager;
                } else {
                  const created = managerResponse?.createdUsers?.find(
                    (u) =>
                      u.email?.trim().toLowerCase() === action.managerEmail!.trim().toLowerCase(),
                  );
                  if (created) {
                    resolvedManager = {
                      id: created.id,
                      name: created.fullName,
                      email: created.email,
                    };
                  }
                }

                if (!resolvedManager) {
                  return of({
                    membersResponse,
                    managerResponse,
                    updatedGroup: undefined,
                    resolvedManager: undefined,
                  });
                }

                return this.groupService.getForEdit(createdGroup.id).pipe(
                  switchMap((fullEditGroup) => {
                    const editGroup: EditGroup = {
                      ...fullEditGroup,
                      companyId: action.companyId,
                      managers: [resolvedManager!.id] as any[],
                      attributes: (fullEditGroup.attributes ?? []).map((a) => ({
                        ...a,
                        AttributeDefinitionId: a.attributeId,
                      })) as any,
                    };

                    return this.groupService.updateRuleBasedGroup(createdGroup.id, editGroup).pipe(
                      map((res) => ({
                        membersResponse,
                        managerResponse,
                        updatedGroup: res.body,
                        resolvedManager,
                      })),
                    );
                  }),
                );
              }),
              // 4. Dispatch state actions
              mergeMap(({ membersResponse, managerResponse, updatedGroup, resolvedManager }) => {
                const resultActions: (
                  | ReturnType<typeof OrganizationActions.addGroupSuccess>
                  | ReturnType<typeof OrganizationActions.createUsersSuccess>
                  | ReturnType<typeof OrganizationActions.addUsersToGroup>
                  | ReturnType<typeof OrganizationActions.editGroupSuccess>
                  | ReturnType<typeof OrganizationActions.moveUsers>
                  | ReturnType<typeof OrganizationActions.updateValidationStatus>
                )[] = [];

                // Add group to state tree
                resultActions.push(
                  OrganizationActions.addGroupSuccess({
                    hierarchyId: action.hierarchyId,
                    categoryId: action.group.categoryId,
                    parentId: action.parentGroupId,
                    group: createdGroup,
                    leaf: true,
                    manager: resolvedManager,
                  }),
                );

                // Add newly created member users to state
                if (membersResponse) {
                  resultActions.push(
                    OrganizationActions.createUsersSuccess({ response: membersResponse }),
                  );

                  if (membersResponse.createdUsers.length > 0) {
                    resultActions.push(
                      OrganizationActions.addUsersToGroup({
                        hierarchyId: action.hierarchyId,
                        groupId: createdGroup.id,
                        users: membersResponse.createdUsers,
                      }),
                    );
                  }
                }

                // Add newly created manager user to state
                if (managerResponse) {
                  resultActions.push(
                    OrganizationActions.createUsersSuccess({ response: managerResponse }),
                  );

                  // Only add manager to the group in state if they are also a member
                  if (action.managerIsAlsoMember && managerResponse.createdUsers.length > 0) {
                    resultActions.push(
                      OrganizationActions.addUsersToGroup({
                        hierarchyId: action.hierarchyId,
                        groupId: createdGroup.id,
                        users: managerResponse.createdUsers,
                      }),
                    );
                  }
                }

                // Move existing users into the new group
                if (action.existingMemberIdsToAdd && action.existingMemberIdsToAdd.length > 0) {
                  resultActions.push(
                    OrganizationActions.moveUsers({
                      companyId: action.companyId,
                      hierarchyId: action.hierarchyId,
                      newGroupId: createdGroup.id,
                      userIds: action.existingMemberIdsToAdd,
                    }),
                  );
                }

                // Update group in state tree with manager info
                if (updatedGroup && resolvedManager) {
                  resultActions.push(
                    OrganizationActions.editGroupSuccess({
                      group: updatedGroup,
                      manager: resolvedManager,
                    }),
                  );
                }

                // Reset validationStatus so it doesn't interfere with dialogs
                // that subscribe to it (e.g. UserComponent auto-closes on 'valid')
                resultActions.push(
                  OrganizationActions.updateValidationStatus({
                    validationStatus: ValidationStatusEnum.idle,
                  }),
                );

                return resultActions;
              }),
            );
          }),
          catchError((error) => {
            if (error?.error?.error === 'A group with the selected external ID already exists.') {
              return of(OrganizationActions.addGroupValidationFail({ error: error?.error?.error }));
            }
            return of(OrganizationActions.addGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  deleteUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteUsers),
      switchMap((action) =>
        this.userService.GDPRDeleteUsers(action.companyId, action.model).pipe(
          map((usersHttpResponse) => {
            return OrganizationActions.deleteUsersSuccess({
              userIds: usersHttpResponse.body,
              hierarchyId: action.model.hierarchyId,
            });
          }),
          catchError((error) => {
            return of(OrganizationActions.deleteUsersError({ error }));
          }),
        ),
      ),
    ),
  );

  deleteManagerFromTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteManagerFromTeam),
      switchMap((action) => {
        const noOneManager = { id: 0, name: '', email: '' };

        const updateTeams$ =
          action.teamsToUnmanage.length > 0
            ? forkJoin(
                action.teamsToUnmanage.map((group) =>
                  this.groupService.update(group.id, group).pipe(
                    map((res) =>
                      OrganizationActions.editGroupSuccess({
                        group: res.body,
                        manager: noOneManager,
                        currentManagerId: action.currentManagerId,
                      }),
                    ),
                  ),
                ),
              )
            : of([] as ReturnType<typeof OrganizationActions.editGroupSuccess>[]);

        return updateTeams$.pipe(
          switchMap((editGroupActions) => [
            ...editGroupActions,
            OrganizationActions.deleteUsers({
              companyId: action.companyId,
              model: {
                companyId: action.companyId,
                hierarchyId: action.hierarchyId,
                userIds: [action.userId],
              },
            }),
          ]),
          catchError((error) => of(OrganizationActions.deleteUsersError({ error }))),
        );
      }),
    ),
  );

  editRuleBasedTeamWithUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editRuleBasedTeamWithUsers),
      switchMap((action) => {
        const managerIsNew = action.managerEmail && !action.manager?.id;

        // 1a. Create member users (as members of the team)
        const createMembers$ =
          action.usersRequest && action.usersRequest.users.length > 0
            ? this.userService.createUsers({ ...action.usersRequest, groupId: action.group.id })
            : of(undefined as CreateUsersResponse | undefined);

        // 1b. Create manager user separately (if new)
        // If manager is also a member → groupId is team (belongs to team)
        // If manager is NOT a member → groupId is parentGroupId (belongs to parent/top group)
        const createManager$ = managerIsNew
          ? this.userService.createUsers({
              users: [action.managerUserRequest ?? { email: action.managerEmail }],
              groupId: action.managerIsAlsoMember ? action.group.id : action.parentGroupId,
            })
          : of(undefined as CreateUsersResponse | undefined);

        return forkJoin({
          membersResponse: createMembers$,
          managerResponse: createManager$,
        }).pipe(
          switchMap(({ membersResponse, managerResponse }) => {
            const resultActions: Observable<
              | ReturnType<typeof OrganizationActions.createUsersSuccess>
              | ReturnType<typeof OrganizationActions.addUsersToGroup>
              | ReturnType<typeof OrganizationActions.editGroupSuccess>
              | ReturnType<typeof OrganizationActions.moveUsersSuccess>
              | ReturnType<typeof OrganizationActions.removeUsers>
              | ReturnType<typeof OrganizationActions.editUsersSuccess>
            >[] = [];

            // Emit createUsersSuccess for members
            if (membersResponse) {
              resultActions.push(
                of(OrganizationActions.createUsersSuccess({ response: membersResponse })),
              );

              if (membersResponse.createdUsers.length > 0) {
                resultActions.push(
                  of(
                    OrganizationActions.addUsersToGroup({
                      hierarchyId: action.hierarchyId,
                      groupId: action.group.id,
                      users: membersResponse.createdUsers,
                    }),
                  ),
                );
              }
            }

            // Emit createUsersSuccess for manager
            if (managerResponse) {
              resultActions.push(
                of(OrganizationActions.createUsersSuccess({ response: managerResponse })),
              );

              // Only add manager to the group in state if they are also a member
              if (action.managerIsAlsoMember && managerResponse.createdUsers.length > 0) {
                resultActions.push(
                  of(
                    OrganizationActions.addUsersToGroup({
                      hierarchyId: action.hierarchyId,
                      groupId: action.group.id,
                      users: managerResponse.createdUsers,
                    }),
                  ),
                );
              }
            }

            // 2. Resolve manager (may have been just created)
            let resolvedManager: { id: number; name: string; email: string } | undefined;

            if (!action.managerEmail) {
              resolvedManager = undefined;
            } else if (action.manager && action.manager.id > 0) {
              resolvedManager = action.manager;
            } else {
              const created = managerResponse?.createdUsers?.find(
                (u) => u.email?.trim().toLowerCase() === action.managerEmail!.trim().toLowerCase(),
              );
              if (created) {
                resolvedManager = {
                  id: created.id,
                  name: created.fullName,
                  email: created.email,
                };
              } else {
                resolvedManager = action.manager;
              }
            }

            // 3. Update rule-based group with resolved manager
            const editGroup: EditGroup = {
              ...action.group,
              managers:
                resolvedManager && resolvedManager.id > 0 ? ([resolvedManager.id] as any[]) : [],
              attributes: (action.group.attributes ?? []).map((a) => ({
                ...a,
                AttributeDefinitionId: a.attributeId,
              })) as any,
            };

            resultActions.push(
              this.groupService.updateRuleBasedGroup(action.group.id, editGroup).pipe(
                map((groupHttpResponse) =>
                  OrganizationActions.editGroupSuccess({
                    group: groupHttpResponse.body,
                    manager: resolvedManager,
                    currentManagerId: action.currentManagerId,
                  }),
                ),
              ),
            );

            // 4. Move existing users into the team
            if (action.existingUserIdsToAdd.length > 0) {
              resultActions.push(
                this.userService
                  .move({
                    companyId: action.companyId,
                    hierarchyId: action.hierarchyId,
                    newGroupId: action.group.id,
                    userIds: action.existingUserIdsToAdd,
                  })
                  .pipe(
                    map((users) =>
                      OrganizationActions.moveUsersSuccess({
                        hierarchyId: action.hierarchyId,
                        groupId: action.group.id,
                        userIds: action.existingUserIdsToAdd,
                        usersAfterMove: users,
                      }),
                    ),
                  ),
              );
            }

            // 5. Remove users no longer in the team and clean up their attributes
            if (action.userIdsToRemove.length > 0) {
              const attributeIds = (action.group.attributes ?? [])
                .map((a: any) => a.attributeDefinitionId)
                .filter((id: number) => !!id);

              const removeAttributes$ =
                attributeIds.length > 0
                  ? this.userService.removeUsersAttributes({
                      userIds: action.userIdsToRemove,
                      attributeIds,
                    })
                  : of([] as any[]);

              resultActions.push(
                removeAttributes$.pipe(
                  mergeMap((updatedUsers) => {
                    const actions: (
                      | ReturnType<typeof OrganizationActions.removeUsers>
                      | ReturnType<typeof OrganizationActions.editUsersSuccess>
                    )[] = [
                      OrganizationActions.removeUsers({
                        userIds: action.userIdsToRemove,
                        hierarchyId: action.hierarchyId,
                        groupId: action.group.id,
                      }),
                    ];

                    if (updatedUsers.length > 0) {
                      actions.push(OrganizationActions.editUsersSuccess({ users: updatedUsers }));
                    }

                    return actions;
                  }),
                ),
              );
            }

            // Reset validationStatus after all actions complete so it doesn't
            // interfere with dialogs that subscribe to it (e.g. UserComponent)
            return concat(
              merge(...resultActions),
              of(
                OrganizationActions.updateValidationStatus({
                  validationStatus: ValidationStatusEnum.idle,
                }),
              ),
            );
          }),
          catchError((error) => of(OrganizationActions.editGroupError({ error }))),
        );
      }),
    ),
  );
}
